/**
 * Cookie consent: what the visitor decided, and how the rest of the app finds out.
 *
 * Deliberately not a vendor. Four EU markets means four legal reviews of the wording,
 * and a third-party consent script is itself a third-party script - the one thing a
 * consent banner should not be introducing. The wording lives in the CMS
 * (`Cookie Consent`, per region and language); this module only stores the answer.
 *
 * Three rules the implementation has to keep:
 *
 * 1. **Nothing runs before the answer.** No analytics, no marketing tag, no cookie
 *    beyond what the site cannot work without. That is why analytics is loaded on
 *    consent rather than loaded-and-disabled.
 * 2. **Refusing is exactly as easy as accepting.** Both are one click, side by side.
 *    That is a legal requirement, not a design preference.
 * 3. **A decision can be changed.** The footer reopens the banner, and withdrawing
 *    consent clears the cookies that were set under it.
 *
 * The record is kept in localStorage rather than a cookie: there is no server render
 * to read a cookie, and storing the answer to "may we store things" in the one place
 * that needs no permission is the tidier bargain.
 */

export type ConsentCategory = 'analytics' | 'marketing';

export interface ConsentState {
  /** The `version` of the wording that was agreed to. */
  version: string;
  analytics: boolean;
  marketing: boolean;
  /** When the decision was made, ISO 8601. */
  at: string;
}

const KEY = 'weco.consent';

type Listener = (state: ConsentState | null) => void;
const listeners = new Set<Listener>();

/** Private-mode browsers throw on storage access rather than returning null. */
function safeRead(): string | null {
  try { return window.localStorage.getItem(KEY); } catch { return null; }
}

function safeWrite(value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, value);
  } catch { /* storage unavailable: the banner simply asks again next time */ }
}

export function readConsent(): ConsentState | null {
  const raw = safeRead();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (typeof parsed.version !== 'string') return null;
    return {
      version: parsed.version,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      at: typeof parsed.at === 'string' ? parsed.at : new Date().toISOString()
    };
  } catch {
    return null;
  }
}

function notify(state: ConsentState | null) {
  for (const listener of listeners) listener(state);
}

export function saveConsent(decision: { version: string; analytics: boolean; marketing: boolean }): ConsentState {
  const state: ConsentState = { ...decision, at: new Date().toISOString() };
  safeWrite(JSON.stringify(state));
  notify(state);
  return state;
}

/** Forget the decision entirely, so the banner asks again. */
export function clearConsent() {
  safeWrite(null);
  notify(null);
}

export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Whether the banner still has to be answered.
 *
 * A stored decision only counts against the wording it was given for: if the CMS text
 * has been rewritten and its `version` bumped, the old answer is not consent to the new
 * text and the visitor is asked again.
 */
export function needsDecision(state: ConsentState | null, version: string): boolean {
  return !state || state.version !== version;
}

export function hasConsent(state: ConsentState | null, category: ConsentCategory): boolean {
  return Boolean(state && state[category]);
}

// --- reopening the banner from elsewhere ------------------------------------

type OpenListener = () => void;
const openListeners = new Set<OpenListener>();

/** Called by the footer's "cookie settings" link. */
export function openConsentSettings() {
  for (const listener of openListeners) listener();
}

export function onOpenConsentSettings(listener: OpenListener): () => void {
  openListeners.add(listener);
  return () => { openListeners.delete(listener); };
}

/**
 * Delete the cookies a category set, by name prefix.
 *
 * Only reaches first-party cookies on this domain, which is all a page can do - a
 * third party's cookies are the third party's to drop. Withdrawing consent therefore
 * stops the collection and clears what it can, and the docs say so rather than
 * implying more.
 */
export function dropCookies(prefixes: string[]) {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  // Cookies may be scoped to the host or to the registrable domain, and a delete only
  // works when the domain matches, so try both.
  const domains = [undefined, host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];

  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0]?.trim();
    if (!name || !prefixes.some((prefix) => name.startsWith(prefix))) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }
}
